import { PARTNER_ROLES } from '../Constants/partnerRoles';
import { INTERNAL_SE_PARTNER_PORTAL_PAE_EMAILS } from '../Constants/partnerAccessConstants';

const normalize = (value) => String(value || '').trim().toLowerCase();
const normalizeCompact = (value) => normalize(value).replace(/[^a-z0-9]/g, '');

const getTypeOfUser = (currentUser) => {
  const rawType = currentUser?.typeOfUser
    || currentUser?.type_of_user
    || currentUser?.userType
    || currentUser?.user_type
    || '';
  return normalize(rawType);
};

const getCompactTypeOfUser = (currentUser) => (
  normalizeCompact(
    currentUser?.typeOfUser
    || currentUser?.type_of_user
    || currentUser?.userType
    || currentUser?.user_type,
  )
);

const getRoleOfUser = (currentUser) => normalize(
  currentUser?.roleOfUser
  || currentUser?.role_of_user
  || currentUser?.role,
);
const getCompactRoleOfUser = (currentUser) => normalizeCompact(
  currentUser?.roleOfUser
  || currentUser?.role_of_user
  || currentUser?.role,
);

const matchesLeadership = (...values) => (
  values.some((value) => /leadership/i.test(String(value || '')))
);

const matchesPAE = (...values) => (
  values.some((value) => /^pae$/i.test(String(value || '').trim()) || /pae/i.test(String(value || '')))
);

const getUserEmail = (currentUser) => normalize(
  currentUser?.email
  || currentUser?.user_email
  || currentUser?.userEmail
  || currentUser?.username,
);

const INTERNAL_SE_PARTNER_PORTAL_PAE_EMAIL_SET = new Set(
  INTERNAL_SE_PARTNER_PORTAL_PAE_EMAILS.map((email) => email.trim().toLowerCase()),
);

export const partnerRoleService = {
  getTypeOfUser(currentUser) {
    return getTypeOfUser(currentUser);
  },

  isLeadershipUser(currentUser) {
    if (!currentUser) {
      return false;
    }

    return matchesLeadership(
      currentUser.typeOfUser,
      currentUser.type_of_user,
      currentUser.userType,
      currentUser.user_type,
      currentUser.roleOfUser,
      currentUser.role_of_user,
    );
  },

  isPAEUser(currentUser) {
    if (!currentUser) {
      return false;
    }

    const typeOfUser = getTypeOfUser(currentUser);
    const compactType = getCompactTypeOfUser(currentUser);
    const roleOfUser = getRoleOfUser(currentUser);
    const compactRole = getCompactRoleOfUser(currentUser);

    return (
      typeOfUser === 'pae'
      || compactType === 'pae'
      || compactType.includes('pae')
      || roleOfUser === 'pae'
      || compactRole.includes('pae')
      || matchesPAE(
        currentUser.typeOfUser,
        currentUser.roleOfUser,
        currentUser.role_of_user,
        currentUser.role,
      )
      || this.isInternalSEPartnerPortalPAEUser(currentUser)
    );
  },

  isAdminUser(currentUser) {
    if (!currentUser) {
      return false;
    }

    const typeOfUser = getTypeOfUser(currentUser);
    const roleOfUser = getRoleOfUser(currentUser);

    return typeOfUser === 'admin' || roleOfUser === 'admin';
  },

  isPAERoleUser(currentUser) {
    if (!currentUser) {
      return false;
    }

    const roleOfUser = String(
      currentUser.roleOfUser
      || currentUser.role_of_user
      || '',
    ).trim();

    return roleOfUser.toUpperCase() === 'PAE';
  },

  isPartnerUser(currentUser) {
    if (!currentUser) {
      return false;
    }

    const typeOfUser = getTypeOfUser(currentUser);
    const compactType = getCompactTypeOfUser(currentUser);
    return typeOfUser === 'external se' || compactType === 'externalse';
  },

  isInternalSEPartnerPortalPAEUser(currentUser) {
    if (!currentUser) {
      return false;
    }

    const email = getUserEmail(currentUser);
    const typeOfUser = getTypeOfUser(currentUser);
    return typeOfUser === 'internal se'
      && Boolean(email)
      && INTERNAL_SE_PARTNER_PORTAL_PAE_EMAIL_SET.has(email);
  },

  canSubmitAsPartner(currentUser) {
    if (!currentUser) {
      return false;
    }

    return this.isPartnerUser(currentUser);
  },

  canSubmitOnBehalf(currentUser) {
    return this.canPerformPAEOperations(currentUser);
  },

  canAccessSubmitProject(currentUser) {
    return this.canSubmitAsPartner(currentUser) || this.canSubmitOnBehalf(currentUser);
  },

  canSubmitPartnerProject(currentUser) {
    return this.canAccessSubmitProject(currentUser);
  },

  canPerformPAEOperations(currentUser) {
    if (!currentUser) {
      return false;
    }

    const role = this.resolveRole(currentUser);
    if ([PARTNER_ROLES.PAE, PARTNER_ROLES.LEADERSHIP, PARTNER_ROLES.ADMIN].includes(role)) {
      return true;
    }

    return this.isLeadershipUser(currentUser) || this.isPAEUser(currentUser);
  },

  hasPartnerPortalAccess(roleOrUser) {
    if (roleOrUser && typeof roleOrUser === 'object') {
      return this.isAdminUser(roleOrUser)
        || this.isPAEUser(roleOrUser)
        || this.isLeadershipUser(roleOrUser)
        || this.isPartnerUser(roleOrUser)
        || this.isInternalSEPartnerPortalPAEUser(roleOrUser);
    }

    return roleOrUser === PARTNER_ROLES.ADMIN
      || roleOrUser === PARTNER_ROLES.PAE
      || roleOrUser === PARTNER_ROLES.LEADERSHIP;
  },

  resolveRole(currentUser) {
    const typeOfUser = getTypeOfUser(currentUser);
    const compactType = getCompactTypeOfUser(currentUser);
    const roleOfUser = getRoleOfUser(currentUser);
    const compactRole = getCompactRoleOfUser(currentUser);
    const rawTypeOfUser = String(
      currentUser?.typeOfUser
      || currentUser?.type_of_user
      || currentUser?.userType
      || currentUser?.user_type
      || '',
    ).trim();

    if (typeOfUser === 'admin' || roleOfUser === 'admin') {
      return PARTNER_ROLES.ADMIN;
    }
    if (
      typeOfUser === 'leadership'
      || compactType.includes('leadership')
      || roleOfUser === 'leadership'
      || compactRole.includes('leadership')
      || matchesLeadership(rawTypeOfUser, currentUser?.roleOfUser, currentUser?.role_of_user)
    ) {
      return PARTNER_ROLES.LEADERSHIP;
    }
    if (
      typeOfUser === 'pae'
      || compactType === 'pae'
      || compactType.includes('pae')
      || roleOfUser === 'pae'
      || compactRole.includes('pae')
    ) {
      return PARTNER_ROLES.PAE;
    }
    if (
      typeOfUser === 'external se'
      || compactType === 'externalse'
    ) {
      return PARTNER_ROLES.EXTERNAL_SE;
    }

    if (this.isInternalSEPartnerPortalPAEUser(currentUser)) {
      return PARTNER_ROLES.PAE;
    }

    return null;
  },
};
